import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { BaseComponent } from '@app/core/helperclasses/basecomponent';
import {
  SURVEY_SERVICE,
  SurveyServiceInterface,
} from '@app/core/survey-service-interface';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
  PendingSurvey,
  SurveyMultipleChoiceQuestion,
  SurveyQuestion,
  SurveyRatingQuestion,
  SurveyResponseValue,
  SurveySingleChoiceQuestion,
  bucketRating,
  computeUniformPathLength,
} from './survey.types';

const THANK_YOU_AUTO_CLOSE_MS = 2500;

@UntilDestroy()
@Component({
  selector: 'survey-popover',
  templateUrl: './survey-popover.component.html',
  styleUrls: ['./survey-popover.component.scss'],
})
export class SurveyPopoverComponent extends BaseComponent implements OnInit {
  public pending: PendingSurvey | null = null;
  public currentQuestionIndex = 0;
  public currentStep = 1;
  public pathTotal: number | null = null;
  public showThankYou = false;
  public form: FormGroup;
  public responses: Record<string, SurveyResponseValue> = {};
  public hoveredRating: number | null = null;
  private hasMarkedShownFor: string | null = null;
  private thankYouTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private _fb: FormBuilder,
    @Inject(SURVEY_SERVICE) private surveyService: SurveyServiceInterface,
  ) {
    super();
    this.form = this._fb.group({});
  }

  ngOnInit(): void {
    this.addSubscription(this.surveyService.activeSurvey$, {
      filterUndefined: false,
    }).subscribe((pending) => {
      if (pending) {
        this.onSurveyOpen(pending as PendingSurvey);
      } else {
        this.onSurveyClose();
      }
    });
  }

  // ---------- lifecycle helpers ----------

  private onSurveyOpen(pending: PendingSurvey): void {
    this.pending = pending;
    this.currentQuestionIndex = 0;
    this.currentStep = 1;
    this.pathTotal = computeUniformPathLength(pending.survey.questions, 0);
    this.responses = {};
    this.showThankYou = false;
    this.form = this._fb.group({});
    this.ensureControlForCurrent();

    if (this.hasMarkedShownFor !== pending.survey.id) {
      this.hasMarkedShownFor = pending.survey.id;
      this.surveyService.markSurveyShown(pending);
    }
  }

  private onSurveyClose(): void {
    this.pending = null;
    this.showThankYou = false;
    this.hasMarkedShownFor = null;
    if (this.thankYouTimer) {
      clearTimeout(this.thankYouTimer);
      this.thankYouTimer = null;
    }
  }

  // ---------- template helpers ----------

  get currentQuestion(): SurveyQuestion | null {
    if (!this.pending) return null;
    return this.pending.survey.questions[this.currentQuestionIndex] ?? null;
  }

  control(questionId: string): AbstractControl {
    return this.form.get(questionId) as AbstractControl;
  }

  ratingValues(question: SurveyRatingQuestion): number[] {
    return Array.from({ length: question.scale }, (_, i) => i + 1);
  }

  isRatingActive(question: SurveyRatingQuestion, value: number): boolean {
    return this.responses[question.id] === value;
  }

  isStarFilled(question: SurveyRatingQuestion, value: number): boolean {
    if (this.hoveredRating !== null) {
      return value <= this.hoveredRating;
    }
    const selected = this.responses[question.id];
    return typeof selected === 'number' && value <= selected;
  }

  onRatingHover(value: number): void {
    this.hoveredRating = value;
  }

  onRatingLeave(): void {
    this.hoveredRating = null;
  }

  isChoiceActive(
    question: SurveySingleChoiceQuestion,
    choice: string,
  ): boolean {
    return this.responses[question.id] === choice;
  }

  isMultiChoiceActive(
    question: SurveyMultipleChoiceQuestion,
    choice: string,
  ): boolean {
    const v = this.responses[question.id];
    return Array.isArray(v) && v.includes(choice);
  }

  get showStepIndicator(): boolean {
    return this.pathTotal !== null && this.pathTotal > 1;
  }

  get showSubmitButton(): boolean {
    const q = this.currentQuestion;
    if (!q) return false;
    if ('skipSubmitButton' in q && q.skipSubmitButton) return false;
    return true;
  }

  get isSubmitDisabled(): boolean {
    const q = this.currentQuestion;
    if (!q) return true;
    if (q.optional) return false;
    const value = this.responses[q.id];
    if (value === undefined || value === null) return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'string') return value.trim().length === 0;
    return false;
  }

  // ---------- user actions ----------

  onRatingSelect(question: SurveyRatingQuestion, value: number): void {
    this.responses[question.id] = value;
    this.control(question.id)?.setValue(value);
    if (question.skipSubmitButton) {
      this.advance();
    }
  }

  onSingleChoiceSelect(
    question: SurveySingleChoiceQuestion,
    choice: string,
  ): void {
    this.responses[question.id] = choice;
    this.control(question.id)?.setValue(choice);
  }

  onMultiChoiceToggle(
    question: SurveyMultipleChoiceQuestion,
    choice: string,
  ): void {
    const existing = this.responses[question.id];
    const list = Array.isArray(existing) ? [...existing] : [];
    const idx = list.indexOf(choice);
    if (idx >= 0) list.splice(idx, 1);
    else list.push(choice);
    this.responses[question.id] = list;
    this.control(question.id)?.setValue(list);
  }

  onOpenInput(question: SurveyQuestion, value: string): void {
    this.responses[question.id] = value;
  }

  onLinkClick(question: SurveyQuestion): void {
    this.responses[question.id] = 'link clicked';
    this.advance();
  }

  onSkip(): void {
    const q = this.currentQuestion;
    if (!q || !q.optional) return;
    this.responses[q.id] = null;
    this.advance();
  }

  onNext(): void {
    this.advance();
  }

  onClose(): void {
    if (!this.pending) return;
    this.surveyService.dismissSurvey(this.pending);
  }

  onThankYouClose(): void {
    this.onSurveyClose();
  }

  // ---------- branching engine ----------

  private advance(): void {
    const q = this.currentQuestion;
    if (!q || !this.pending) return;
    const value = this.responses[q.id];
    const nextIndex = this.computeNextIndex(q, value);
    if (nextIndex === null) {
      this.submit();
      return;
    }
    if (nextIndex >= this.pending.survey.questions.length) {
      this.submit();
      return;
    }
    this.currentQuestionIndex = nextIndex;
    this.currentStep++;
    this.hoveredRating = null;
    this.ensureControlForCurrent();
  }

  private computeNextIndex(
    question: SurveyQuestion,
    value: SurveyResponseValue,
  ): number | null {
    const total = this.pending?.survey.questions.length ?? 0;
    const branching = question.branching;

    if (!branching) {
      const next = this.currentQuestionIndex + 1;
      return next < total ? next : null;
    }

    if (branching.type === 'end') return null;

    if (branching.type === 'specific_question') {
      return branching.index;
    }

    // response_based
    let key: string | undefined;
    if (question.type === 'rating' && typeof value === 'number') {
      key = bucketRating(question.scale, !!question.isNpsQuestion, value);
    } else if (question.type === 'single_choice' && typeof value === 'string') {
      key = value;
    } else if (
      question.type === 'multiple_choice' &&
      Array.isArray(value) &&
      value.length > 0
    ) {
      key = value[0];
    }

    if (!key) return null;
    const resolved = branching.responseValues[key];
    if (resolved === undefined) {
      const next = this.currentQuestionIndex + 1;
      return next < total ? next : null;
    }
    if (resolved === 'end') return null;
    return resolved;
  }

  private submit(): void {
    if (!this.pending) return;
    const pending = this.pending;
    this.surveyService.submitSurveyResponse(pending, this.responses);

    if (pending.survey.appearance?.displayThankYouMessage) {
      // submitSurveyResponse already nulled out activeSurvey$, but we want the
      // thank-you panel to keep rendering — re-instate `pending` locally.
      this.pending = pending;
      this.showThankYou = true;
      this.thankYouTimer = setTimeout(
        () => this.onSurveyClose(),
        THANK_YOU_AUTO_CLOSE_MS,
      );
    } else {
      this.onSurveyClose();
    }
  }

  private ensureControlForCurrent(): void {
    const q = this.currentQuestion;
    if (!q) return;
    if (this.form.get(q.id)) return;

    const validators = q.optional ? [] : [Validators.required];
    const initial: SurveyResponseValue = q.type === 'multiple_choice' ? [] : '';
    this.form.addControl(q.id, new FormControl(initial, validators));
  }
}
